import React, { useEffect, useState } from 'react';
import { ArrowRight } from 'lucide-react';

const APP_STORE_URL = 'https://apps.apple.com/app/id6751061434';

function useCounter(target, duration = 1800, start = false) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime = null;
    const step = (ts) => {
      if (!startTime) startTime = ts;
      const progress = Math.min((ts - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.floor(eased * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [start, target, duration]);
  return value;
}

const Hero = () => {
  const [statsVisible, setStatsVisible] = useState(false);
  const commitments = useCounter(2500, 1600, statsVisible);

  return (
    <section className="relative min-h-screen flex items-center justify-center pt-32 pb-20 px-6 lg:px-12 overflow-hidden">
      <div className="cyberpunk-bg"></div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">

          {/* Left — Copy */}
          <div className="space-y-8 hero-copy">
            <div className="section-label">😈 ADHD-Friendly · iOS</div>

            <h1 className="text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight">
              <span className="block whitespace-nowrap">The commitment app</span>
              <span className="gradient-text">that holds you to it.</span>
            </h1>

            <p className="text-lg lg:text-xl text-[rgba(255,255,255,0.6)] leading-relaxed max-w-lg">
              Set a timer. Make a promise. Face the result.
              <br />
              TickBoom records every commitment you make to yourself — and whether you kept it.
            </p>

            <div className="flex flex-col sm:flex-row items-start gap-4 pt-2">
              <a
                href={APP_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary"
              >
                Download on App Store
                <ArrowRight size={18} />
              </a>
              <p className="text-sm text-[#6b7280] self-center">
                Free to download · Quick sign-up · iOS
              </p>
            </div>

            {/* Stats */}
            <div
              className="grid grid-cols-3 gap-8 pt-10 border-t border-[rgba(255,255,255,0.08)]"
              ref={(el) => {
                if (el && !statsVisible) {
                  const obs = new IntersectionObserver(([e]) => {
                    if (e.isIntersecting) { setStatsVisible(true); obs.disconnect(); }
                  }, { threshold: 0.5 });
                  obs.observe(el);
                }
              }}
            >
              <div>
                <div className="text-3xl font-bold gradient-text font-mono">
                  {statsVisible ? `${(commitments / 1000).toFixed(1)}K+` : '0'}
                </div>
                <div className="text-xs text-[#6b7280] uppercase tracking-widest mt-1.5">Commitments</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-white font-mono">87%</div>
                <div className="text-xs text-[#6b7280] uppercase tracking-widest mt-1.5">Success Rate</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-white font-mono">50K+</div>
                <div className="text-xs text-[#6b7280] uppercase tracking-widest mt-1.5">Hours Focused</div>
              </div>
            </div>
          </div>

          {/* Right — App Mockup */}
          <div className="relative flex justify-center lg:justify-end items-center min-h-[580px] hero-phone">
            <div className="relative w-full max-w-[260px] lg:max-w-[280px] phone-float">
              {/* Glow */}
              <div
                className="absolute inset-0 rounded-[40px]"
                style={{
                  background: 'radial-gradient(ellipse at center, rgba(124, 58, 237, 0.18) 0%, rgba(0, 210, 106, 0.08) 60%, transparent 70%)',
                  transform: 'scale(1.5)',
                  filter: 'blur(40px)',
                }}
              ></div>

              {/* Phone frame */}
              <div
                className="relative rounded-[36px] p-3 transition-transform duration-500 hover:scale-105"
                style={{
                  background: '#0c0c14',
                  border: '1px solid rgba(124, 58, 237, 0.25)',
                  boxShadow: '0 32px 64px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(124, 58, 237, 0.12)',
                }}
              >
                <img
                  src="https://customer-assets.emergentagent.com/job_cyber-countdown/artifacts/f2l7wkkb_IMG_8830.PNG"
                  alt="TickBoom App Interface"
                  className="w-full rounded-[28px]"
                  style={{ imageRendering: 'auto' }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
